// Ops notifications. The historical name stays so the ~250 call sites need no edit:
// Notifications.sendNotification(channel, title, ...) no longer sends to LogSnag. It routes the event
// through src/utils/notify (routing table -> daily-summary counter, or a silent / loud message in the
// Outfame Telegram group's Billing or Disputes topic). Server only; in the browser it is a no-op.

/** LogSnag rejects the WHOLE event (HTTP 400 "Tag values may not be longer than 160 characters")
 *  when any tag value is longer than this, so over-long strings are cut with an ellipsis. */
const MAX_TAG_VALUE_LENGTH = 160

function truncateTagValue<T>(value: T): T | string {
    if (typeof value !== "string" || value.length <= MAX_TAG_VALUE_LENGTH) return value
    let cut = value.slice(0, MAX_TAG_VALUE_LENGTH - 1)
    // Never leave half of a surrogate pair (emoji) dangling at the cut.
    if (/[\uD800-\uDBFF]$/.test(cut)) cut = cut.slice(0, -1)
    return `${cut}…`
}

/** Convert any key format (camelCase, PascalCase, snake_case, etc.) to lowercase kebab-case
 *  and ensure values are primitives. LogSnag API requires tag keys to contain only
 *  lowercase alphabet characters and dashes — no numbers, underscores, or spaces. */
export function sanitizeTags(data: Record<string, any>): Record<string, string | number | boolean> {
    return Object.fromEntries(
        Object.entries(data)
            .filter(([, v]) => v !== undefined && v !== null)
            .map(([key, value]) => [
                key
                    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")   // camelCase boundary (e.g. myKey -> my-Key)
                    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1-$2") // consecutive caps (e.g. XMLParser -> XML-Parser)
                    .replace(/_/g, "-")             // snake_case -> snake-case
                    .replace(/\s+/g, "-")           // spaces -> dashes
                    .toLowerCase()                  // ensure lowercase
                    .replace(/[^a-z-]/g, "")        // strip everything except lowercase letters and dashes
                    .replace(/--+/g, "-")           // collapse double dashes
                    .replace(/^-/, "")              // strip leading dash
                    .replace(/-$/, ""),              // strip trailing dash
                truncateTagValue(typeof value === "object" ? JSON.stringify(value) : value)
            ])
            .filter(([key]) => key.length > 0)     // drop entries with empty keys after sanitization
    )
}

export class Notifications {
    /** Never rejects and never blocks the caller: routing (Redis + a possible Telegram post with one
     *  429 wait of up to 30 s) runs in the background under Vercel's waitUntil. Returns true when the
     *  event was handed over (false only in the browser or if scheduling itself threw). */
    public static async sendNotification(channel: string, title: string, icon = "💰", notify = false, userId?: string | null, data?: any): Promise<boolean> {
        if (typeof window !== "undefined") return false
        try {
            const work = (async () => {
                const { routeNotification } = await import("@/utils/notify/notify")
                const out = await routeNotification({ channel, title, data: data && typeof data === "object" ? data : undefined, userId })
                if (out.delivery !== "none" || out.dest !== "DROP") console.log(`[notify] ${channel} / ${title} -> ${out.rule ?? "unrouted"} ${out.dest} ${out.delivery}`)
            })()
            const { runInBackground } = await import("@/utils/notify/notify")
            runInBackground(work)
            return true
        } catch (error) {
            console.error("[notify] sendNotification failed:", error)
            return false
        }
    }

    public static async sendTestNotification() {
        Notifications.sendNotification("testing-channel", "testing-event", "✨", true, "123", { hello: true, welcome: 1 })
    }
}
